// CSV import utilities for template bulk creation
// Handles template parsing, validation, CSV parsing with auto-detection of separators
// Support for multi-line format with template headers and task items
// Support for recurrence rules (daily, weekly with days, monthly with day of month)
// CHANGE: Created for template CSV import feature
// CHANGE: Updated to use createCSVBlob for proper UTF-8 BOM handling
// CHANGE: Removed all debug console statements for production

import { supabase } from './supabase';
import type { Site } from './supabase';
import { getTemplates, createTemplateWithItems } from './templates';
import { saveRecurrence, validateRecurrenceConfig } from './recurrence';
import { createCSVBlob } from './csvEncoding';

export interface CSVTaskItem {
  title: string;
  orderIndex: number;
}

export interface CSVTemplateRow {
  lineNumber: number;
  templateName: string;
  description: string;
  estimatedDuration: number | null;
  serviceName: string;
  items: CSVTaskItem[];
  // Recurrence fields
  recurrenceFrequency: 'daily' | 'weekly' | 'monthly' | 'yearly' | '' | null;
  recurrenceDays: number[]; // For weekly: 1-7 (Mon-Sun)
  recurrenceDayOfMonth: number | null; // For monthly/yearly: 1-31
  recurrenceInterval: number; // Interval (default 1)
  recurrenceMonthOfYear: number | null; // For yearly: 1-12
  validationErrors: string[];
  isValid: boolean;
  finalName: string; // Name after duplicate resolution
}

export interface ParsedTemplateCSVResult {
  templates: CSVTemplateRow[];
  errors: string[];
  warnings: string[];
}

// Detect CSV separator automatically (comma, semicolon, or tab)
export function detectCSVSeparator(csvContent: string): string {
  const firstLine = csvContent.split(/\r?\n/)[0];

  // Count occurrences of each potential separator
  const separators = [',', ';', '\t'];
  const counts = separators.map(sep => ({
    separator: sep,
    count: (firstLine.match(new RegExp(`\\${sep}`, 'g')) || []).length
  }));

  // Sort by count descending and return the most frequent one
  counts.sort((a, b) => b.count - a.count);

  // Return comma as default if no separator found
  return counts[0].count > 0 ? counts[0].separator : ',';
}

// Parse CSV line handling quoted values with specified separator
function parseCSVLine(line: string, separator: string = ','): string[] {
  const values: string[] = [];
  let currentValue = '';
  let insideQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      insideQuotes = !insideQuotes;
    } else if (char === separator && !insideQuotes) {
      values.push(currentValue.trim());
      currentValue = '';
    } else {
      currentValue += char;
    }
  }

  values.push(currentValue.trim());
  return values;
}

// Parse recurrence days string (e.g., "1,2,3" or "1;2;3")
function parseRecurrenceDays(daysStr: string): number[] {
  if (!daysStr || !daysStr.trim()) return [];

  // Split by comma or semicolon
  const days = daysStr.split(/[,;]/).map(d => {
    const num = parseInt(d.trim(), 10);
    return isNaN(num) ? null : num;
  }).filter(d => d !== null) as number[];

  // Validate days are in range 1-7
  return days.filter(d => d >= 1 && d <= 7);
}

// Validate recurrence configuration
function validateRecurrence(
  frequency: string,
  days: number[],
  dayOfMonth: number | null,
  interval: number,
  monthOfYear: number | null
): string | null {
  if (!frequency) return null; // No recurrence is valid

  // Validate interval
  if (interval < 1 || interval > 365) {
    return "L'intervalle doit être entre 1 et 365";
  }

  switch (frequency.toLowerCase()) {
    case 'daily':
      return null;

    case 'weekly':
      if (days.length === 0) {
        return 'Récurrence hebdomadaire nécessite au moins un jour (1-7)';
      }
      return null;

    case 'monthly':
      if (dayOfMonth === null || dayOfMonth < 1 || dayOfMonth > 31) {
        return 'Récurrence mensuelle nécessite un jour du mois (1-31)';
      }
      return null;

    case 'yearly':
      if (monthOfYear === null || monthOfYear < 1 || monthOfYear > 12) {
        return "Récurrence annuelle nécessite un mois de l'année (1-12)";
      }
      if (dayOfMonth === null || dayOfMonth < 1 || dayOfMonth > 31) {
        return 'Récurrence annuelle nécessite un jour du mois (1-31)';
      }
      return null;

    default:
      return `Fréquence de récurrence invalide: ${frequency}`;
  }
}

// Resolve duplicate template names by adding suffix
async function resolveDuplicateNames(
  templates: CSVTemplateRow[]
): Promise<void> {
  // Get existing template names
  const existingTemplates = await getTemplates(false);
  const existingNames = new Set(
    existingTemplates.map(t => t.name.toLowerCase())
  );

  // Track names in the current import batch
  const batchNames = new Map<string, number>();

  for (const template of templates) {
    let finalName = template.templateName;
    let counter = 2;

    // Check against existing templates
    while (existingNames.has(finalName.toLowerCase())) {
      finalName = `${template.templateName} (${counter})`;
      counter++;
    }

    // Check against current batch
    const lowerName = finalName.toLowerCase();
    if (batchNames.has(lowerName)) {
      const batchCounter = batchNames.get(lowerName)! + 1;
      finalName = `${template.templateName} (${batchCounter})`;
      batchNames.set(lowerName, batchCounter);
    } else {
      batchNames.set(lowerName, 1);
    }

    // Add to existing names for next iteration
    existingNames.add(finalName.toLowerCase());
    template.finalName = finalName;

    // Add warning if name was changed
    if (finalName !== template.templateName) {
      template.validationErrors.push(
        `Template renommé en "${finalName}" (doublon détecté)`
      );
    }
  }
}

// Parse CSV content with multi-line format
// Format: Template header line followed by task item lines
// Template line starts with "TEMPLATE," or is first non-empty line
// Task item lines start with "TASK," or are continuation lines
export async function parseTemplateCSV(
  csvContent: string,
  existingServices: Array<{ id: string; name: string }>
): Promise<ParsedTemplateCSVResult> {
  const lines = csvContent.split(/\r?\n/);
  const templates: CSVTemplateRow[] = [];
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check if CSV has content
  if (lines.length === 0 || lines.every(l => !l.trim())) {
    errors.push('Le fichier CSV est vide');
    return { templates: [], errors, warnings };
  }

  // Detect CSV separator automatically
  const separator = detectCSVSeparator(csvContent);

  // Create service name lookup (case-insensitive)
  const serviceMap = new Map<string, string>();
  existingServices.forEach(s => {
    serviceMap.set(s.name.toLowerCase(), s.id);
  });

  // Parse header
  const headerLine = lines[0];
  const headers = headerLine.split(separator).map(h => h.trim().toLowerCase());

  // Expected columns for template header
  const templateColumns = [
    'template_name',
    'description',
    'estimated_duration',
    'service',
    'recurrence_frequency',
    'recurrence_interval',
    'recurrence_days',
    'recurrence_day_of_month',
    'recurrence_month'
  ];

  // Expected columns for task items
  const taskColumns = ['task_title'];

  // Validate header has required columns
  if (!headers.includes('template_name')) {
    errors.push('Colonne manquante: template_name');
    return { templates: [], errors, warnings };
  }

  // Get column indices
  const templateNameIdx = headers.indexOf('template_name');
  const descriptionIdx = headers.indexOf('description');
  const durationIdx = headers.indexOf('estimated_duration');
  const serviceIdx = headers.indexOf('service');
  const recurrenceFreqIdx = headers.indexOf('recurrence_frequency');
  const recurrenceIntervalIdx = headers.indexOf('recurrence_interval');
  const recurrenceDaysIdx = headers.indexOf('recurrence_days');
  const recurrenceDayOfMonthIdx = headers.indexOf('recurrence_day_of_month');
  const recurrenceMonthIdx = headers.indexOf('recurrence_month');
  const taskTitleIdx = headers.indexOf('task_title');

  if (taskTitleIdx === -1) {
    errors.push('Colonne manquante: task_title');
    return { templates: [], errors, warnings };
  }

  // Parse data rows
  let currentTemplate: CSVTemplateRow | null = null;
  let taskOrderIndex = 0;
  let templateCount = 0;

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line || line.startsWith('#')) continue; // Skip empty lines and comments

    const lineNumber = i + 1;
    const values = parseCSVLine(line, separator);

    // Check if this is a template header line (has template_name)
    const templateName = templateNameIdx >= 0 ? (values[templateNameIdx]?.trim() || '') : '';

    if (templateName) {
      // Save previous template if exists
      if (currentTemplate) {
        templates.push(currentTemplate);
      }

      // Check template limit
      templateCount++;
      if (templateCount > 100) {
        warnings.push('Limite de 100 templates atteinte, lignes suivantes ignorées');
        break;
      }

      // Parse template data
      const description = descriptionIdx >= 0 ? (values[descriptionIdx]?.trim() || '') : '';
      const durationStr = durationIdx >= 0 ? (values[durationIdx]?.trim() || '') : '';
      const serviceName = serviceIdx >= 0 ? (values[serviceIdx]?.trim() || '') : '';
      const recurrenceFreq = recurrenceFreqIdx >= 0 ?
        (values[recurrenceFreqIdx]?.trim().toLowerCase() || '') : '';
      const recurrenceIntervalStr = recurrenceIntervalIdx >= 0 ?
        (values[recurrenceIntervalIdx]?.trim() || '') : '';
      const recurrenceDaysStr = recurrenceDaysIdx >= 0 ?
        (values[recurrenceDaysIdx]?.trim() || '') : '';
      const recurrenceDayOfMonthStr = recurrenceDayOfMonthIdx >= 0 ?
        (values[recurrenceDayOfMonthIdx]?.trim() || '') : '';
      const recurrenceMonthStr = recurrenceMonthIdx >= 0 ?
        (values[recurrenceMonthIdx]?.trim() || '') : '';

      // Parse duration
      let estimatedDuration: number | null = null;
      if (durationStr) {
        const parsed = parseInt(durationStr, 10);
        if (!isNaN(parsed) && parsed > 0) {
          estimatedDuration = parsed;
        }
      }

      // Parse recurrence
      const recurrenceDays = parseRecurrenceDays(recurrenceDaysStr);
      const recurrenceDayOfMonth = recurrenceDayOfMonthStr ?
        parseInt(recurrenceDayOfMonthStr, 10) : null;
      const recurrenceInterval = recurrenceIntervalStr ?
        parseInt(recurrenceIntervalStr, 10) : 1;
      const recurrenceMonthOfYear = recurrenceMonthStr ?
        parseInt(recurrenceMonthStr, 10) : null;

      const validationErrors: string[] = [];

      // Validate template name
      if (!templateName) {
        validationErrors.push('Nom du template manquant');
      }

      // Validate service if provided
      let serviceId: string | null = null;
      if (serviceName) {
        serviceId = serviceMap.get(serviceName.toLowerCase()) || null;
        if (!serviceId) {
          validationErrors.push(`Service introuvable: ${serviceName}`);
        }
      }

      // Validate recurrence
      if (recurrenceFreq) {
        const recurrenceError = validateRecurrence(
          recurrenceFreq,
          recurrenceDays,
          recurrenceDayOfMonth,
          recurrenceInterval,
          recurrenceMonthOfYear
        );
        if (recurrenceError) {
          validationErrors.push(recurrenceError);
        }
      }

      // Create template object
      currentTemplate = {
        lineNumber,
        templateName,
        description,
        estimatedDuration,
        serviceName,
        items: [],
        recurrenceFrequency: recurrenceFreq as any || null,
        recurrenceDays,
        recurrenceDayOfMonth: !isNaN(recurrenceDayOfMonth!) ? recurrenceDayOfMonth : null,
        recurrenceInterval: !isNaN(recurrenceInterval) ? recurrenceInterval : 1,
        recurrenceMonthOfYear: !isNaN(recurrenceMonthOfYear!) ? recurrenceMonthOfYear : null,
        validationErrors,
        isValid: validationErrors.length === 0,
        finalName: templateName // Will be updated during duplicate resolution
      };

      taskOrderIndex = 0;

      // Check if task is on same line
      const taskTitle = taskTitleIdx >= 0 ? (values[taskTitleIdx]?.trim() || '') : '';
      if (taskTitle) {
        currentTemplate.items.push({
          title: taskTitle,
          orderIndex: taskOrderIndex++
        });
      }
    } else {
      // This is a task item line for the current template
      if (!currentTemplate) {
        errors.push(`Ligne ${lineNumber}: Item de tâche sans template parent`);
        continue;
      }

      const taskTitle = taskTitleIdx >= 0 ? (values[taskTitleIdx]?.trim() || '') : '';

      if (taskTitle) {
        currentTemplate.items.push({
          title: taskTitle,
          orderIndex: taskOrderIndex++
        });
      }
    }
  }

  // Add last template if exists
  if (currentTemplate) {
    templates.push(currentTemplate);
  }

  // Validate that all templates have at least one task
  templates.forEach(template => {
    if (template.items.length === 0) {
      template.validationErrors.push('Aucune tâche définie pour ce template');
      template.isValid = false;
    }
  });

  // Resolve duplicate names
  await resolveDuplicateNames(templates);

  return { templates, errors, warnings };
}

// Import templates into database
export async function importTemplates(
  templates: CSVTemplateRow[],
  existingServices: Array<{ id: string; name: string }>
): Promise<{
  success: number;
  errors: Array<{ templateName: string; error: string }>;
}> {
  let successCount = 0;
  const importErrors: Array<{ templateName: string; error: string }> = [];

  // Create service name lookup
  const serviceMap = new Map<string, string>();
  existingServices.forEach(s => {
    serviceMap.set(s.name.toLowerCase(), s.id);
  });

  for (const template of templates) {
    if (!template.isValid) {
      importErrors.push({
        templateName: template.finalName,
        error: template.validationErrors.join(', ')
      });
      continue;
    }

    try {
      // Get service ID if service name provided
      const serviceId = template.serviceName ?
        serviceMap.get(template.serviceName.toLowerCase()) : null;

      // Create template with items
      const createdTemplate = await createTemplateWithItems(
        {
          name: template.finalName,
          description: template.description || undefined,
          estimated_duration_minutes: template.estimatedDuration || undefined,
          service_id: serviceId || undefined
        },
        template.items.map(item => ({
          title: item.title,
          order_index: item.orderIndex,
          // Default values for other fields
          requires_text_input: false,
          text_input_placeholder: null,
          is_text_input_required: false,
        }))
      );

      // Create recurrence rule if specified
      if (template.recurrenceFrequency) {
        await saveRecurrence(createdTemplate.id, {
          frequency: template.recurrenceFrequency,
          days_of_week: template.recurrenceDays,
          day_of_month: template.recurrenceDayOfMonth,
          interval: template.recurrenceInterval,
          month_of_year: template.recurrenceMonthOfYear
        });
      }

      successCount++;
    } catch (error) {
      importErrors.push({
        templateName: template.finalName,
        error: error instanceof Error ? error.message : 'Erreur inconnue'
      });
    }
  }

  return { success: successCount, errors: importErrors };
}

// Generate a sample CSV template
export function generateTemplateCSVTemplate(): string {
  const headers = 'template_name,description,estimated_duration,service,recurrence_frequency,recurrence_interval,recurrence_days,recurrence_day_of_month,recurrence_month,task_title';
  const examples = [
    'Nettoyage quotidien,Routine de nettoyage journalière,30,Nettoyage,daily,1,,,Vider les poubelles',
    ',,,,,,,,Nettoyer les surfaces',
    ',,,,,,,,Passer l\'aspirateur',
    'Nettoyage tous les 2 jours,Nettoyage espacé,30,Nettoyage,daily,2,,,Vider les corbeilles',
    'Inspection hebdomadaire,Inspection de sécurité,60,Maintenance,weekly,1,"1,3,5",,Vérifier les extincteurs',
    ',,,,,,,,Contrôler les issues de secours',
    'Réunion bimensuelle,Réunion équipe,90,Administration,weekly,2,1,,Préparer ordre du jour',
    'Maintenance mensuelle,Maintenance équipements,120,Maintenance,monthly,1,,15,Révision climatisation',
    ',,,,,,,,Test alarmes incendie',
    'Inventaire trimestriel,Inventaire complet,240,Gestion,monthly,3,,1,Compter stock',
    'Audit annuel,Audit de conformité,480,Administration,yearly,1,,15,3,Préparer documents',
    ',,,,,,,,Vérifier conformité'
  ];

  return headers + '\n' + examples.join('\n');
}

// Download template CSV file
export function downloadTemplateCSVTemplate(): void {
  const content = generateTemplateCSVTemplate();
  // Use createCSVBlob to add UTF-8 BOM for proper Excel compatibility
  const blob = createCSVBlob(content);
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', 'modele_import_templates.csv');
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// Generate CSV with import results
export function generateTemplateResultsCSV(
  results: Array<{
    templateName: string;
    itemCount: number;
    recurrence: string;
    error?: string;
  }>
): string {
  const headers = 'template_name,item_count,recurrence,status,error';
  const rows = results.map(r => {
    const status = r.error ? 'ERREUR' : 'SUCCÈS';
    const error = r.error ? r.error.replace(/,/g, ';') : '';
    return `"${r.templateName}",${r.itemCount},"${r.recurrence}",${status},"${error}"`;
  });

  return headers + '\n' + rows.join('\n');
}

// Download results CSV
export function downloadTemplateResultsCSV(
  results: Array<{
    templateName: string;
    itemCount: number;
    recurrence: string;
    error?: string;
  }>,
  filename: string = 'resultats_import_templates.csv'
): void {
  const content = generateTemplateResultsCSV(results);
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
